import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/checkout.css";

const FREE_SHIPPING_THRESHOLD = siteConfig.shippingThreshold || 200;
const formatPrice = (value) =>
  `${siteConfig.currency.symbol}${Number(value).toFixed(2)} ${siteConfig.currency.code}`;

const initialFields = {
  email: "",
  country: "United States",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
  phone: "",
  cardNumber: "",
  expiry: "",
  securityCode: "",
  cardName: "",
  billingFirstName: "",
  billingLastName: "",
  billingAddress: "",
  billingCity: "",
  billingRegion: "",
  billingPostalCode: "",
};

const fieldLabels = {
  email: "Email address",
  firstName: "First name",
  lastName: "Last name",
  address: "Address",
  city: "City",
  region: "State / Province",
  postalCode: "ZIP / postal code",
  cardNumber: "Card number",
  expiry: "Expiration date",
  securityCode: "Security code",
  cardName: "Name on card",
};

const billingFieldLabels = {
  billingFirstName: "Billing first name",
  billingLastName: "Billing last name",
  billingAddress: "Billing address",
  billingCity: "Billing city",
  billingRegion: "Billing state / province",
  billingPostalCode: "Billing ZIP / postal code",
};

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [fields, setFields] = useState(initialFields);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [billingSame, setBillingSame] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const standardShipping =
    subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : siteConfig.shippingRates.standard;
  const shipping =
    shippingMethod === "express"
      ? siteConfig.shippingRates.express
      : standardShipping;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "cardNumber") {
      nextValue = value
        .replace(/\D/g, "")
        .slice(0, 16)
        .replace(/(.{4})/g, "$1 ")
        .trim();
    }
    if (name === "expiry") {
      const numbers = value.replace(/\D/g, "").slice(0, 4);
      nextValue = numbers.length > 2 ? `${numbers.slice(0, 2)} / ${numbers.slice(2)}` : numbers;
    }
    if (name === "securityCode") {
      nextValue = value.replace(/\D/g, "").slice(0, 4);
    }

    setFields((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    Object.entries(fieldLabels).forEach(([field, label]) => {
      if (!fields[field].trim()) nextErrors[field] = `${label} is required.`;
    });
    if (!billingSame) {
      Object.entries(billingFieldLabels).forEach(([field, label]) => {
        if (!fields[field].trim()) nextErrors[field] = `${label} is required.`;
      });
    }
    if (fields.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (fields.cardNumber.replace(/\s/g, "").length !== 16) {
      nextErrors.cardNumber = "Enter a 16-digit demo card number.";
    }
    if (!/^(0[1-9]|1[0-2])\s\/\s\d{2}$/.test(fields.expiry)) {
      nextErrors.expiry = "Use MM / YY format.";
    }
    if (fields.securityCode.length < 3) {
      nextErrors.securityCode = "Enter a 3 or 4-digit code.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) {
      window.requestAnimationFrame(() => {
        document.querySelector(".checkout-field.is-error input")?.focus();
      });
      return;
    }

    setIsSubmitting(true);
    const orderNumber = `BW-${Date.now().toString().slice(-8)}`;
    window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: {
          orderNumber,
          email: fields.email,
          total,
        },
      });
      window.scrollTo({ top: 0 });
    }, 850);
  };

  if (lines.length === 0) {
    return (
      <main className="checkout-empty">
        <p className="eyebrow">Checkout</p>
        <h1>Your cart is waiting for an adventure.</h1>
        <p>Add something to your cart before continuing to checkout.</p>
        <Link className="button button--primary" to="/shop">
          Shop the collection
        </Link>
      </main>
    );
  }

  const renderField = ({ name, label, type = "text", placeholder, autoComplete }) => (
    <label className={`checkout-field${errors[name] ? " is-error" : ""}`}>
      <span>{label}</span>
      <input
        type={type}
        name={name}
        value={fields[name]}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
        onChange={updateField}
      />
      {errors[name] && (
        <small id={`${name}-error`} role="alert">
          {errors[name]}
        </small>
      )}
    </label>
  );

  return (
    <main className="checkout-page">
      <nav className="checkout-breadcrumb" aria-label="Checkout progress">
        <Link to="/cartpage">Cart</Link>
        <span>›</span>
        <strong>Information</strong>
        <span>›</span>
        <span>Shipping</span>
        <span>›</span>
        <span>Payment</span>
      </nav>

      <header className="checkout-heading">
        <p className="eyebrow">Secure demo checkout</p>
        <h1>Finish the journey.</h1>
        <p>No real payment is processed. Use any 16-digit demo card number.</p>
      </header>

      <form className="checkout-layout" noValidate onSubmit={handleSubmit}>
        <div className="checkout-form">
          <section aria-labelledby="checkout-contact-title">
            <div className="checkout-section-heading">
              <span>01</span>
              <h2 id="checkout-contact-title">Contact</h2>
            </div>
            {renderField({
              name: "email",
              label: "Email address",
              type: "email",
              placeholder: "you@example.com",
              autoComplete: "email",
            })}
          </section>

          <section aria-labelledby="checkout-delivery-title">
            <div className="checkout-section-heading">
              <span>02</span>
              <h2 id="checkout-delivery-title">Delivery</h2>
            </div>
            <label className="checkout-field">
              <span>Country / Region</span>
              <select name="country" value={fields.country} onChange={updateField}>
                <option>United States</option>
                <option>Canada</option>
                <option>Japan</option>
                <option>United Kingdom</option>
                <option>Australia</option>
                <option>Hong Kong SAR</option>
              </select>
            </label>
            <div className="checkout-form__row">
              {renderField({
                name: "firstName",
                label: "First name",
                autoComplete: "given-name",
              })}
              {renderField({
                name: "lastName",
                label: "Last name",
                autoComplete: "family-name",
              })}
            </div>
            {renderField({
              name: "company",
              label: "Company (optional)",
              autoComplete: "organization",
            })}
            {renderField({
              name: "address",
              label: "Address",
              autoComplete: "street-address",
            })}
            {renderField({
              name: "apartment",
              label: "Apartment, suite, etc. (optional)",
              autoComplete: "address-line2",
            })}
            <div className="checkout-form__row checkout-form__row--three">
              {renderField({ name: "city", label: "City", autoComplete: "address-level2" })}
              {renderField({
                name: "region",
                label: "State / Province",
                autoComplete: "address-level1",
              })}
              {renderField({
                name: "postalCode",
                label: "ZIP / postal code",
                autoComplete: "postal-code",
              })}
            </div>
            {renderField({
              name: "phone",
              label: "Phone (optional)",
              type: "tel",
              autoComplete: "tel",
            })}
          </section>

          <section aria-labelledby="checkout-shipping-title">
            <div className="checkout-section-heading">
              <span>03</span>
              <h2 id="checkout-shipping-title">Shipping method</h2>
            </div>
            <div className="checkout-shipping-options">
              <label>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="standard"
                  checked={shippingMethod === "standard"}
                  onChange={() => setShippingMethod("standard")}
                />
                <span>
                  <strong>Ground shipping</strong>
                  <small>3–6 business days</small>
                </span>
                <b>{standardShipping === 0 ? "Free" : formatPrice(standardShipping)}</b>
              </label>
              <label>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="express"
                  checked={shippingMethod === "express"}
                  onChange={() => setShippingMethod("express")}
                />
                <span>
                  <strong>Express shipping</strong>
                  <small>1–2 business days</small>
                </span>
                <b>{formatPrice(siteConfig.shippingRates.express)}</b>
              </label>
            </div>
          </section>

          <section aria-labelledby="checkout-payment-title">
            <div className="checkout-section-heading">
              <span>04</span>
              <h2 id="checkout-payment-title">Payment</h2>
            </div>
            <div className="checkout-payment-note">
              <span>Demo credit card</span>
              <div aria-label="Accepted card types">
                <i>VISA</i><i>MC</i><i>AMEX</i>
              </div>
            </div>
            <div className="checkout-payment-fields">
              {renderField({
                name: "cardNumber",
                label: "Card number",
                placeholder: "0000 0000 0000 0000",
                autoComplete: "cc-number",
              })}
              <div className="checkout-form__row">
                {renderField({
                  name: "expiry",
                  label: "Expiration date",
                  placeholder: "MM / YY",
                  autoComplete: "cc-exp",
                })}
                {renderField({
                  name: "securityCode",
                  label: "Security code",
                  placeholder: "CVV",
                  autoComplete: "cc-csc",
                })}
              </div>
              {renderField({
                name: "cardName",
                label: "Name on card",
                autoComplete: "cc-name",
              })}
            </div>
            <label className="checkout-billing-check">
              <input
                type="checkbox"
                checked={billingSame}
                onChange={(event) => setBillingSame(event.target.checked)}
              />
              <span>Use shipping address as billing address</span>
            </label>
            {!billingSame && (
              <div className="checkout-billing-fields" aria-label="Billing address">
                <div className="checkout-form__row">
                  {renderField({
                    name: "billingFirstName",
                    label: "First name",
                    autoComplete: "billing given-name",
                  })}
                  {renderField({
                    name: "billingLastName",
                    label: "Last name",
                    autoComplete: "billing family-name",
                  })}
                </div>
                {renderField({
                  name: "billingAddress",
                  label: "Billing address",
                  autoComplete: "billing street-address",
                })}
                <div className="checkout-form__row checkout-form__row--three">
                  {renderField({
                    name: "billingCity",
                    label: "City",
                    autoComplete: "billing address-level2",
                  })}
                  {renderField({
                    name: "billingRegion",
                    label: "State / Province",
                    autoComplete: "billing address-level1",
                  })}
                  {renderField({
                    name: "billingPostalCode",
                    label: "ZIP / postal code",
                    autoComplete: "billing postal-code",
                  })}
                </div>
              </div>
            )}
          </section>

          <button className="checkout-submit checkout-submit--mobile" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating order…" : `Place demo order · ${formatPrice(total)}`}
          </button>
        </div>

        <aside className="checkout-summary">
          <p className="eyebrow">Order summary</p>
          <div className="checkout-summary__lines">
            {lines.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div className="checkout-summary__image">
                  <img src={item.product.image} alt={item.product.name} />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <h3>{item.product.name}</h3>
                  <p>{item.color} / {item.size}</p>
                </div>
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
              </article>
            ))}
          </div>
          {location.state?.orderNote && (
            <div className="checkout-summary__note">
              <strong>Order note</strong>
              <p>{location.state.orderNote}</p>
            </div>
          )}
          <div className="checkout-summary__totals">
            <div><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></div>
            <div><span>Shipping</span><strong>{shipping === 0 ? "Free" : formatPrice(shipping)}</strong></div>
            <div><span>Total</span><strong>{formatPrice(total)}</strong></div>
          </div>
          <button className="checkout-submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating order…" : "Place demo order"}
          </button>
          <p className="checkout-summary__legal">
            By placing this demo order, you agree to our <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>.
          </p>
        </aside>
      </form>
    </main>
  );
};

export default CheckOut;
